import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChange,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';

import { BsModalComponent } from 'ng2-bs3-modal';
import { partition } from 'lodash';
import { FormControl } from '@angular/forms';

import { ToolbarButtonDefinition } from '@shared/types';
import { ReplaySubject, rx, Subscription } from '@shared/rx';
import { onControlIsInvalidChanged } from '@shared/rx-operators';
import { I18n } from '@app/i18n/strings-keys';

// 2 is backdrop click
// 3 is esc
const BOOTSTRAP_MODAL_ON_DISMISS_RESULTS = [2, 3];
const HEADER_BUTTON_VALUES = ['ok', 'cancel', 'done', 'save'];
const OK_BUTTON_INDEX = 0;

@Component({
  selector: 'xvi-modal-wrapper',
  templateUrl: 'modal-wrapper.component.html',
  styleUrls: ['modal-wrapper.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModalWrapperComponent implements OnChanges, OnInit, OnDestroy {
  @Input() public title: string;
  @Input() public control: FormControl;
  @Input() public widgetData: {
    cssClass?: string;
    keyboard?: boolean;
    backdrop?: boolean | 'static';
  } = {};
  @Input() public isShowModal: boolean;
  @Input() public buttons: ToolbarButtonDefinition[] = [
    {
      value: 'ok',
      caption: I18n.Common.OK,
      class: 'btn-primary pull-right',
      disabled: false,
      click: (): void => this.onSubmit.emit(),
    },
    {
      value: 'cancel',
      caption: I18n.Common.Cancel,
      class: 'btn-default pull-right',
      click: (): void => this.onCancel.emit(),
    },
  ];
  @Input() public modalId: string;

  @Output() public onSubmit: EventEmitter<any> = new EventEmitter<any>();
  @Output() public onCancel: EventEmitter<any> = new EventEmitter<any>();

  public headerButtons: ToolbarButtonDefinition[] = [];
  public footerButtons: ToolbarButtonDefinition[] = [];

  @ViewChild('XVIModalWrapper', { static: true })
  private modalWindow: BsModalComponent;

  private subscriptions: Subscription[] = [];
  private control$: ReplaySubject<FormControl> = new ReplaySubject<FormControl>(
    1,
  );

  public ngOnInit(): void {
    /**
     * onDismiss eventEmitter doesn't work correctly for now
     */
    this.subscriptions.push(
      this.modalWindow.onHide
        .pipe(
          rx.filter(({ type }) =>
            BOOTSTRAP_MODAL_ON_DISMISS_RESULTS.includes(type),
          ),
        )
        .subscribe(() => this.onCancel.emit()),

      onControlIsInvalidChanged(this.control$).subscribe((isInvalid) => {
        this.buttons = [...this.buttons];
        this.buttons[OK_BUTTON_INDEX] = {
          ...this.buttons[OK_BUTTON_INDEX],
          disabled: isInvalid,
        };
      }),
    );

    this.refreshButtons(); // Handling the case when default value of 'buttons' is not changed.
  }

  public ngOnChanges(
    changes: Record<keyof ModalWrapperComponent, SimpleChange>,
  ): void {
    if (changes.isShowModal) {
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      this.isShowModal ? this.modalWindow.open() : this.modalWindow.close();
    }

    if (changes.control) this.control$.next(this.control);
    if (changes.buttons) this.refreshButtons();
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  private refreshButtons(): void {
    [this.headerButtons, this.footerButtons] = partition(
      this.buttons,
      (button) => HEADER_BUTTON_VALUES.includes(button.value),
    );
  }
}
